import axios from 'axios';
import { PaymentRequest, PaymentIntentRequest, SetupIntentRequest, PAYMENT_STATUS, Payment } from '@finpay-payment-v3/payment-types';
import { getPaymentApiBaseUrl, getPaymentApiPathPrefix, getStripeInstance } from './api-config';

export interface CreatePaymentParams {
  programId: number;
  paymentRequest: PaymentRequest;
}

export interface CreatePaymentResult {
  success: boolean;
  message: string;
  paymentIntentId?: string;
  clientSecret?: string;
  status?: string;
  payment?: Payment;
}

export const createPayment = async (
  params: CreatePaymentParams
): Promise<CreatePaymentResult> => {
  const { programId, paymentRequest } = params;

  try {
    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/program/${programId}/payment`;
    
    const response = await axios.post(url, paymentRequest);

    const entity = response.data;
    if (!entity) {
      return {
        success: false,
        message: 'Failed to create payment.',
      };
    }

    const paymentId = entity.paymentId || entity.id || entity.paymentIntentId || entity.extPaymentIntentId || entity.client_secret?.split('_secret_')[0];

    if (!paymentId) {
      return {
        success: false,
        message: 'Payment created but no payment ID returned.',
      };
    }

    const paymentStatus = (entity.status || entity.paymentStatus || '');
    const isPending = paymentStatus.toLowerCase() === PAYMENT_STATUS.PENDING.toLowerCase();
    const successMessage = isPending 
      ? 'Payment submitted, please check back later for status.'
      : 'Payment successful';

    return {
      success: true,
      message: successMessage,
      paymentIntentId: String(paymentId),
      clientSecret: entity.client_secret,
      status: entity.status || entity.paymentStatus,
      payment: entity as Payment,
    };
  } catch (err: any) {
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred creating payment.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

export interface UpdatePaymentIntentParams {
  paymentIntentId: string;
  amount?: number;
  extPaymentUserId?: string;
}

export interface UpdatePaymentIntentResult {
  success: boolean;
  message: string;
  clientSecret?: string;
}

export const updatePaymentIntent = async (
  params: UpdatePaymentIntentParams
): Promise<UpdatePaymentIntentResult> => {
  const { paymentIntentId, amount, extPaymentUserId } = params;

  try {
    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/payment-intent/${paymentIntentId}`;
    
    const response = await axios.patch(url, {
      ...(amount !== undefined && { amount: Math.round(amount * 100) }),
      ...(extPaymentUserId && { customer: extPaymentUserId }),
    });

    const entity = response.data;
    if (!entity) {
      return {
        success: false,
        message: 'Failed to update payment intent.',
      };
    }

    return {
      success: true,
      message: 'Payment intent updated.',
      clientSecret: entity.client_secret,
    };
  } catch (err: any) {
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred updating payment intent.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

export interface CreateAndConfirmPaymentIntentParams extends PaymentIntentRequest {
  paymentMethodId: string;
  confirm?: boolean;
  authToken?: string | null;
}

export interface CreateAndConfirmPaymentIntentResult {
  success: boolean;
  message: string;
  paymentIntentId?: string;
  clientSecret?: string;
  status?: string;
}

export const createAndConfirmPaymentIntent = async (
  params: CreateAndConfirmPaymentIntentParams
): Promise<CreateAndConfirmPaymentIntentResult> => {
  const { amount, paymentMethodId, extPaymentUserId, confirm = true, authToken } = params;

  try {
    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/payment-intent`;
    
    const requestBody = {
      amount: Math.round(amount * 100),
      paymentMethodId,
      extPaymentUserId,
      confirm,
    };

    const response = await axios.post(url, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && {
          Authorization: `Bearer ${authToken}`,
          'x-cog-id': authToken,
        }),
      },
    });

    const entity = response.data;
    if (!entity) {
      return {
        success: false,
        message: 'Failed to create payment intent.',
      };
    }

    const paymentIntentId = entity.id || entity.paymentIntentId;

    return {
      success: true,
      message: confirm ? 'Payment captured successfully.' : 'Payment intent created.',
      paymentIntentId,
      clientSecret: entity.client_secret,
      status: entity.status,
    };
  } catch (err: any) {
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred creating payment intent.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

export interface CreateSetupIntentParams extends SetupIntentRequest {}

export interface CreateSetupIntentResult {
  success: boolean;
  message: string;
  setupIntentId?: string;
  clientSecret?: string;
  status?: string;
}

export const createSetupIntent = async (
  params: CreateSetupIntentParams = {}
): Promise<CreateSetupIntentResult> => {
  const { extPaymentUserId, paymentMethodTypeId } = params;

  try {
    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/setup-intent`;
    
    const requestBody: { 
      extPaymentUserId?: string; 
      paymentMethodTypeId?: number;
    } = {};
    
    if (extPaymentUserId) {
      requestBody.extPaymentUserId = extPaymentUserId;
    }
    
    if (paymentMethodTypeId) {
      requestBody.paymentMethodTypeId = paymentMethodTypeId;
    }

    const response = await axios.post(url, requestBody);

    const entity = response.data;
    if (!entity) {
      return {
        success: false,
        message: 'Failed to create setup intent.',
      };
    }

    const setupIntentId = entity.id || entity.client_secret?.split('_secret_')[0];

    return {
      success: true,
      message: 'Setup intent created.',
      setupIntentId,
      clientSecret: entity.client_secret,
      status: entity.status,
    };
  } catch (err: any) {
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred creating setup intent.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

export interface UpdateSetupIntentParams {
  setupIntentId: string;
  extPaymentUserId?: string;
  paymentMethodTypes?: string[];
}

export interface UpdateSetupIntentResult {
  success: boolean;
  message: string;
  clientSecret?: string;
  status?: string;
}

export const updateSetupIntent = async (
  params: UpdateSetupIntentParams
): Promise<UpdateSetupIntentResult> => {
  const { setupIntentId, extPaymentUserId, paymentMethodTypes } = params;

  try {
    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/setup-intent/${setupIntentId}`;
    
    const requestBody: {
      extPaymentUserId?: string;
      paymentMethodTypes?: string[];
    } = {};

    if (extPaymentUserId) {
      requestBody.extPaymentUserId = extPaymentUserId;
    }

    if (paymentMethodTypes && paymentMethodTypes.length > 0) {
      requestBody.paymentMethodTypes = paymentMethodTypes;
    }

    const response = await axios({
      method: 'PATCH',
      url,
      data: requestBody,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const entity = response.data;
    if (!entity) {
      return {
        success: false,
        message: 'Failed to update setup intent.',
      };
    }

    return {
      success: true,
      message: 'Setup intent updated.',
      clientSecret: entity.client_secret,
      status: entity.status,
    };
  } catch (err: any) {
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred updating setup intent.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

export interface ConfirmACHSetupIntentParams {
  clientSecret: string;
  routingNumber: string;
  accountNumber: string;
  accountHolderType: 'individual' | 'company';
  accountType: 'checking' | 'savings';
  billingDetails: {
    name: string;
    email: string;
  };
}

export interface ConfirmACHSetupIntentResult {
  success: boolean;
  message: string;
  paymentMethodId?: string;
  last4?: string;
  bankName?: string;
  fingerprint?: string;
  status?: string;
}

export const confirmACHSetupIntent = async (
  params: ConfirmACHSetupIntentParams
): Promise<ConfirmACHSetupIntentResult> => {
  const {
    clientSecret,
    routingNumber,
    accountNumber,
    accountHolderType,
    accountType,
    billingDetails,
  } = params;

  try {
    const stripe = await getStripeInstance();
    if (!stripe) {
      return {
        success: false,
        message: 'Stripe has not been initialized.',
      };
    }

    const result = await stripe.confirmUsBankAccountSetup(clientSecret, {
      payment_method: {
        us_bank_account: {
          routing_number: routingNumber,
          account_number: accountNumber,
          account_holder_type: accountHolderType,
          account_type: accountType,
        },
        billing_details: {
          name: billingDetails.name,
          email: billingDetails.email,
        },
      },
    });

    if (result.error) {
      return {
        success: false,
        message: result.error.message || 'Failed to confirm bank account setup.',
      };
    }

    const setupIntent = result.setupIntent;
    if (!setupIntent) {
      return {
        success: false,
        message: 'No setup intent returned.',
      };
    }

    const paymentMethodId = typeof setupIntent.payment_method === 'string' 
      ? setupIntent.payment_method 
      : setupIntent.payment_method?.id;

    if (!paymentMethodId) {
      return {
        success: false,
        message: 'No payment method ID returned.',
      };
    }

    const usBankAccount = typeof setupIntent.payment_method !== 'string' 
      ? setupIntent.payment_method?.us_bank_account 
      : undefined;

    return {
      success: true,
      message: 'Bank account setup confirmed.',
      paymentMethodId,
      last4: usBankAccount?.last4 || accountNumber.slice(-4),
      bankName: usBankAccount?.bank_name,
      fingerprint: usBankAccount?.fingerprint,
      status: setupIntent.status,
    };
  } catch (err: any) {
    return {
      success: false,
      message: err.message || 'An unexpected error occurred.',
    };
  }
};

export interface ConfirmCardSetupIntentParams {
  clientSecret: string;
  cardElement: any;
  billingDetails: {
    name: string;
    email: string;
  };
}

export interface ConfirmCardSetupIntentResult {
  success: boolean;
  message: string;
  paymentMethodId?: string;
  last4?: string;
  brand?: string;
  expMonth?: number;
  expYear?: number;
  fingerprint?: string;
  status?: string;
}

export const confirmCardSetupIntent = async (
  params: ConfirmCardSetupIntentParams
): Promise<ConfirmCardSetupIntentResult> => {
  const { clientSecret, cardElement, billingDetails } = params;

  try {
    const stripe = await getStripeInstance();
    if (!stripe) {
      return {
        success: false,
        message: 'Stripe has not been initialized.',
      };
    }

    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: billingDetails.name,
          email: billingDetails.email,
        },
      },
    });

    if (result.error) {
      return {
        success: false,
        message: result.error.message || 'Failed to confirm card setup.',
      };
    }

    const setupIntent = result.setupIntent;
    if (!setupIntent) {
      return {
        success: false,
        message: 'No setup intent returned.',
      };
    }

    const paymentMethodId = typeof setupIntent.payment_method === 'string' 
      ? setupIntent.payment_method 
      : setupIntent.payment_method?.id;

    if (!paymentMethodId) {
      return {
        success: false,
        message: 'No payment method ID returned.',
      };
    }

    const card = typeof setupIntent.payment_method !== 'string' 
      ? setupIntent.payment_method?.card 
      : undefined;

    return {
      success: true,
      message: 'Card setup confirmed.',
      paymentMethodId,
      last4: card?.last4,
      brand: card?.brand,
      expMonth: card?.exp_month,
      expYear: card?.exp_year,
      fingerprint: card?.fingerprint ?? undefined,
      status: setupIntent.status,
    };
  } catch (err: any) {
    return {
      success: false,
      message: err.message || 'An unexpected error occurred.',
    };
  }
};
